import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { tournamentService } from "../../services/api/tournamentService";
import { getUserSports, getAllSports } from "../../services/api/sportsService";
import GameLayout from "../layout/GameLayout";
import FloatingCard from "../ui/FloatingCard";
import FloatingButton from "../ui/FloatingButton";
import EntityCard from "../ui/EntityCard";
import { canCreateCompetition } from "../../utils/rbac";
import { eid } from "../../utils/entityId";
import { formatDateOnly } from "../../utils/dateOnly";
import FilterBar from "../ui/FilterBar";
import { useUserFilters } from "../../contexts/UserFiltersContext";
import StatusTabs from "../ui/StatusTabs";
import { mineFirst } from "../../utils/listOrder";
import "../../styles/components/TournamentList.css";
import "../../styles/features/tournaments.css";

const STATUS_TABS = [
  { value: "", label: "All" },
  { value: "registration_open", label: "Open" },
  { value: "registration_closed", label: "Closed" },
  { value: "in_progress", label: "In progress" },
  { value: "completed", label: "Completed" },
];

const TournamentList = ({ user, token }) => {
  const navigate = useNavigate();
  const { filterParams, autoExpandRadius, isFiltersReady, needsLocation } = useUserFilters();
  const [tournaments, setTournaments] = useState([]);
  const [sports, setSports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showAccessModal, setShowAccessModal] = useState(false);
  const [accessAction, setAccessAction] = useState("");
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    total: 0,
    pages: 0,
  });

  // Filters state
  const [filters, setFilters] = useState({
    search: "",
    sport_id: "",
    status: "",
    page: 1,
    limit: 12,
  });

  // Tournaments I entered: shown first and marked
  const [entered, setEntered] = useState(() => new Set());
  const signedIn = Boolean(user?.id || token);
  useEffect(() => {
    if (!signedIn) return undefined;
    let alive = true;
    tournamentService.getUserTournamentRegistrations()
      .then((res) => {
        const regs = Array.isArray(res?.data) ? res.data : [];
        if (alive) setEntered(new Set(regs.filter((r) => r.registration_status !== "rejected").map((r) => r.tournament_id)));
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [signedIn]);

  // Load sports for filter dropdown — user's own sports first, fallback to all
  useEffect(() => {
    const loadSports = async () => {
      try {
        const userRes = await getUserSports();
        const data = userRes?.data?.sports || userRes?.sports || userRes?.data || [];
        if (Array.isArray(data) && data.length) { setSports(data); return; }
      } catch { /* fall through */ }
      try {
        const allRes = await getAllSports();
        setSports(Array.isArray(allRes.data) ? allRes.data : []);
      } catch (err) {
        console.error("Failed to load sports:", err);
        setSports([]);
      }
    };
    loadSports();
  }, []);

  const loadTournaments = useCallback(async () => {
    if (!isFiltersReady) return;
    // No location yet: the FilterBar asks for one; never list every region
    if (needsLocation) {
      setTournaments([]);
      setPagination({ page: 1, limit: 12, total: 0, pages: 1 });
      setLoading(false);
      return;
    }
    setLoading(true);
    setError("");

    try {
      // Merge global filters from UserFiltersContext.
      // If the user has explicitly selected a sport in the local dropdown,
      // drop the context's sport_ids so it doesn't block that selection.
      const contextParams = filters.sport_id
        ? { ...filterParams, sport_ids: undefined }
        : filterParams;
      const response = await tournamentService.getTournaments({ ...contextParams, ...filters });
      
      // The list is the response's data, with pagination beside it (TN-B21);
      // older servers nested both one level deeper.
      const list = Array.isArray(response?.data) ? response.data
        : Array.isArray(response?.data?.data) ? response.data.data
        : null;
      if (list) {
        setTournaments(list);
        if (filterParams.lat != null) autoExpandRadius(list.length);
        setPagination(response.pagination || response.data?.pagination || {
          page: 1,
          limit: 12,
          total: list.length,
          pages: 1,
        });
      } else {
        setTournaments([]);
        setPagination({ page: 1, limit: 12, total: 0, pages: 1 });
      }
    } catch (err) {
      console.error("Error loading tournaments:", err);
      setError(err.message || "Failed to load tournaments");
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  }, [filters, filterParams, isFiltersReady, needsLocation]);

  // Load tournaments when filters change
  useEffect(() => {
    loadTournaments();
  }, [loadTournaments]);

  const getCurrentUser = () => {
    if (user) return user;
    try {
      return JSON.parse(localStorage.getItem("user") || "null");
    } catch (error) {
      return null;
    }
  };

  // Only creating is role-based (league_admin / organization_admin /
  // platform_admin / super_admin). Managing a tournament is creator-only and
  // is handled on TournamentDetails (BUG-31).
  const canCreateTournaments = canCreateCompetition(getCurrentUser());

  const openAccessModal = (actionLabel) => {
    setAccessAction(actionLabel);
    setShowAccessModal(true);
  };

  // Handle filter changes
  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
      page: 1, // Reset to first page when filters change
    }));
  };

  // Handle page change
  const handlePageChange = (newPage) => {
    setFilters((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  // Clear filters
  const clearFilters = () => {
    setFilters({
      search: "",
      sport_id: "",
      status: "",
      page: 1,
      limit: 12,
    });
  };

  // Format date
  // Tournament dates are calendar dates — format without timezone shifting.
  const formatDate = (dateString) =>
    formatDateOnly(dateString, { year: "numeric", month: "short", day: "numeric" });

  // "single_elimination" → "Single Elimination"
  const formatType = (type) =>
    type ? type.split("_").map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(" ") : "";

  const myId = getCurrentUser()?.id;
  const isMine = (t) => entered.has(t?.id) || (myId != null && Number(t?.created_by) === Number(myId));
  const shownTournaments = mineFirst(tournaments, new Set(tournaments.filter(isMine).map((t) => t.id)));

  // One flat card per tournament (PR 7b)
  const TournamentCard = ({ tournament }) => {
    if (!tournament) {
      return null;
    }
    const open = () => navigate(`/tournaments/${eid(tournament)}`);
    const start = formatDate(tournament.start_date);
    const end = tournament.end_date ? formatDate(tournament.end_date) : "";
    const dates = end && end !== start ? `${start} – ${end}` : start;
    const venue = tournament.venue || tournament.venue_name;

    return (
      <EntityCard
        className="tournament-list-card"
        title={tournament.name || "Unnamed Tournament"}
        badges={[
          { status: tournament.status || "draft" },
          tournament.sport_name && { label: tournament.sport_name, tone: "sport" },
          isMine(tournament) && { label: Number(tournament.created_by) === Number(myId) ? "⭐ Organizer" : "⭐ Entered", tone: "success" },
        ].filter(Boolean)}
        rows={[
          { key: "dates", icon: "📅", label: "Dates", value: dates || "TBD" },
          tournament.tournament_type && { key: "format", icon: "⚔️", label: "Format", value: formatType(tournament.tournament_type) },
          { key: "teams", icon: "👥", label: "Teams", value: `${tournament.current_participants || 0}/${tournament.max_participants ?? "—"}` },
          venue && { key: "venue", icon: "📍", label: "Venue", value: venue },
        ].filter(Boolean)}
        actions={tournament.status === "registration_open" ? (
          <FloatingButton
            variant="success"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              open();
            }}
            icon="✅"
          >
            <span className="btn-text">Register</span>
          </FloatingButton>
        ) : null}
        onClick={open}
      />
    );
  };

  return (
    <GameLayout
      onLogout={() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }}
      profile={user}
      showFloatingNav={true}
    >
      <div className="tournaments-gaming-layout">
        {/* Gaming Header - Floating */}
        <div className="tournaments-header-gaming">
          <FloatingCard variant="primary" size="large" className="delay-1">
            <div className="header-content-gaming">
              <div className="title-section-gaming">
                <h1 className="tournaments-title-gaming">
                  🏆 Tournament Arena
                </h1>
                <p className="tournaments-subtitle-gaming">
                  Compete in epic tournaments and claim your victory
                </p>
              </div>

              <div className="header-actions-gaming">
                <FloatingButton
                  variant="secondary"
                  size="medium"
                  onClick={() => navigate('/dashboard')}
                  icon="←"
                >
                  <span className="btn-text">Back</span>
                </FloatingButton>
                {user && (
                  <FloatingButton
                    variant="success"
                    size="large"
                    onClick={() => {
                      if (!canCreateTournaments) {
                        openAccessModal("create tournaments");
                        return;
                      }
                      navigate("/tournaments/create");
                    }}
                    icon="⚔️"
                    className="delay-2"
                  >
                    <span className="btn-text">Create Tournament</span>
                  </FloatingButton>
                )}
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Gaming Filters - Floating */}
        <div className="tournaments-filters-gaming">
          <FloatingCard variant="outline" size="medium" className="delay-3">
            <FilterBar inline showSports={false} />
            <div className="filters-content-gaming">
              <h3 className="filters-title-gaming">🔍 Filter Tournaments</h3>
              <StatusTabs
                tabs={STATUS_TABS}
                value={filters.status}
                onChange={(status) => handleFilterChange("status", status)}
                label="Tournament status"
              />

              <div className="filters-grid-gaming">
                <div className="filter-group-gaming">
                  <label>🔎 Search</label>
                  <input
                    type="text"
                    placeholder="Search tournaments..."
                    value={filters.search}
                    onChange={(e) =>
                      handleFilterChange("search", e.target.value)
                    }
                    className="gaming-input"
                  />
                </div>

                <div className="filter-group-gaming">
                  <label>🏆 Sport</label>
                  <select
                    value={filters.sport_id}
                    onChange={(e) =>
                      handleFilterChange("sport_id", e.target.value)
                    }
                    className="gaming-select"
                  >
                    <option value="">All Sports</option>
                    {sports.map((sport) => (
                      <option key={sport.sport_id || sport.id} value={sport.sport_id || sport.id}>
                        {sport.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="filter-actions-gaming">
                  <FloatingButton
                    variant="warning"
                    size="small"
                    onClick={clearFilters}
                    icon="🧹"
                  >
                    <span className="btn-text">Clear Filters</span>
                  </FloatingButton>
                </div>
              </div>
            </div>
          </FloatingCard>
        </div>

        {/* Gaming Messages - Floating */}
        {error && (
          <div className="gaming-message-floating error">
            <span className="message-icon">❌</span>
            <span className="message-text">{error}</span>
          </div>
        )}

        {/* Gaming Loading - Floating */}
        {loading ? (
          <div className="loading-gaming-floating">
            <FloatingCard variant="primary" size="large" className="delay-4">
              <div className="loading-content-gaming">
                <div className="loading-spinner-gaming"></div>
                <h3>Loading Tournament Arena...</h3>
                <p>Preparing epic battles for you</p>
              </div>
            </FloatingCard>
          </div>
        ) : (
          <>
            {/* Gaming Tournaments Grid */}
        {tournaments.length === 0 ? (
          <div className="empty-state-gaming-floating">
            <FloatingCard
              variant="outline"
              size="large"
              className="delay-5"
            >
              <div className="empty-content-gaming">
                <div className="empty-icon">🏟️</div>
                <h3>{needsLocation ? "Tournaments near you" : "No Tournaments Found"}</h3>
                <p>{needsLocation ? "Set your location above to see what's near you." : "The arena awaits your epic tournament creation!"}</p>
                {user && !needsLocation && (
                  <FloatingButton
                    variant="success"
                    size="large"
                    onClick={() => {
                      if (!canCreateTournaments) {
                        openAccessModal("create tournaments");
                        return;
                      }
                      navigate("/tournaments/create");
                    }}
                    icon="⚔️"
                  >
                    <span className="btn-text">
                      Create Your First Tournament
                    </span>
                  </FloatingButton>
                )}
              </div>
            </FloatingCard>
          </div>
        ) : (
          <>
            <div className="tournaments-grid-gaming-floating">
              {shownTournaments
                .filter((tournament) => tournament && tournament.id)
                .map((tournament) => (
                  <TournamentCard
                    key={tournament.id}
                    tournament={tournament}
                  />
                ))}

                {/* Pagination */}
                {pagination.pages > 1 && (
                  <div className="pagination">
                    <button
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page <= 1}
                      className="btn btn-secondary btn-sm"
                    >
                      Previous
                    </button>

                    <div className="page-numbers">
                      {Array.from(
                        { length: Math.min(5, pagination.pages) },
                        (_, i) => {
                          const pageNum = i + Math.max(1, pagination.page - 2);
                          if (pageNum > pagination.pages) return null;

                          return (
                            <button
                              key={pageNum}
                              onClick={() => handlePageChange(pageNum)}
                              className={`btn btn-sm ${
                                pageNum === pagination.page
                                  ? "btn-primary"
                                  : "btn-secondary"
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        }
                      )}
                    </div>

                    <button
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.pages}
                      className="btn btn-secondary btn-sm"
                    >
                      Next
                    </button>
                  </div>
                )}

                {/* Results info */}
                <div className="results-info">
                  Showing {tournaments.length} of {pagination.total} tournaments
                </div>
            </div>
            </>
          )}
          </>
        )}

        {showAccessModal && (
          <div className="modal-overlay-gaming" role="dialog" aria-modal="true">
            <FloatingCard
              size="large"
              transparent={false}
              hover={false}
              className="modal-gaming"
            >
              <div className="modal-header">
                <h3>Access Restricted</h3>
                <button
                  type="button"
                  className="close-button"
                  onClick={() => setShowAccessModal(false)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
              <div className="modal-body">
                <p>
                  You do not have permission to {accessAction}. Request access
                  from your organization admin or upgrade your role.
                </p>
              </div>
              <div className="modal-actions">
                <FloatingButton
                  variant="secondary"
                  size="small"
                  onClick={() => setShowAccessModal(false)}
                >
                  Close
                </FloatingButton>
                <FloatingButton
                  variant="primary"
                  size="small"
                  onClick={() => {
                    setShowAccessModal(false);
                    navigate("/profile");
                  }}
                >
                  Go to Profile
                </FloatingButton>
              </div>
            </FloatingCard>
          </div>
        )}
      </div>
    </GameLayout>
  );
};

export default TournamentList;
